import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BulkUpdateFields, ConditionStatus, ConditionCategory, ConditionPriority, ConditionSource } from '../types';
import { X, Save, Eye, ChevronUp, ChevronDown, CheckCircle2 } from 'lucide-react';
interface BulkEditToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onPreview: (updates: BulkUpdateFields) => void;
  onApply: (updates: BulkUpdateFields) => void;
  uniqueAssignees: string[];
}
export function BulkEditToolbar({
  selectedCount,
  onClearSelection,
  onPreview,
  onApply,
  uniqueAssignees
}: BulkEditToolbarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [updates, setUpdates] = useState<BulkUpdateFields>({});
  // Reset updates when selection is cleared
  useEffect(() => {
    if (selectedCount === 0) {
      setUpdates({});
      setIsExpanded(false);
    } else if (selectedCount > 0 && !isExpanded && Object.keys(updates).length === 0) {
      // Auto-expand when items are first selected
      setIsExpanded(true);
    }
  }, [selectedCount]);
  const handleUpdateChange = (field: keyof BulkUpdateFields, value: any) => {
    setUpdates(prev => {
      const newUpdates = {
        ...prev
      };
      if (value === '' || value === '--None--' || value === null) {
        delete newUpdates[field];
      } else {
        // Handle boolean conversions
        if (value === 'true') newUpdates[field] = true as any;else if (value === 'false') newUpdates[field] = false as any;else newUpdates[field] = value;
      }
      return newUpdates;
    });
  };
  const hasChanges = Object.keys(updates).length > 0;
  const renderSelect = (label: string, field: keyof BulkUpdateFields, options: {
    label: string;
    value: string;
  }[]) => <div className="flex flex-col space-y-1" data-id="element-1">
      <label className="text-xs font-medium text-gray-500" data-id="element-2">{label}</label>
      <select value={updates[field] !== undefined ? String(updates[field]) : ''} onChange={e => handleUpdateChange(field, e.target.value)} className={`block w-full py-1.5 pl-3 pr-8 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444] ${updates[field] !== undefined ? 'border-[#D44444] bg-red-50 text-[#D44444] font-medium' : 'border-gray-300 bg-white text-gray-900'}`} data-id="element-3">
        <option value="" data-id="element-4">-- No Change --</option>
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-5">
            {opt.label}
          </option>)}
      </select>
    </div>;
  const booleanOptions = [{
    label: 'Yes',
    value: 'true'
  }, {
    label: 'No',
    value: 'false'
  }];
  return <AnimatePresence data-id="element-6">
      {selectedCount > 0 && <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" data-id="element-7">
          {/* Header Bar */}
          <div className="flex items-center justify-between px-6 py-3 bg-[#1C1C3A] text-white" data-id="element-8">
            <div className="flex items-center space-x-4" data-id="element-9">
              <span className="flex items-center justify-center w-6 h-6 bg-[#D44444] rounded-full text-xs font-bold" data-id="element-10">
                {selectedCount}
              </span>
              <span className="font-medium" data-id="element-11">Conditions Selected</span>
              <button onClick={onClearSelection} className="text-sm text-gray-300 hover:text-white underline decoration-gray-500 underline-offset-2" data-id="element-12">
                Clear Selection
              </button>
            </div>

            <div className="flex items-center space-x-3" data-id="element-13">
              {hasChanges && <span className="text-sm text-red-300 mr-2 flex items-center" data-id="element-14">
                  <CheckCircle2 className="w-4 h-4 mr-1" data-id="element-15" />
                  {Object.keys(updates).length} fields to update
                </span>}
              <button onClick={() => setIsExpanded(!isExpanded)} className="p-1.5 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors" title={isExpanded ? 'Collapse panel' : 'Expand panel'} data-id="element-16">
                {isExpanded ? <ChevronDown className="w-5 h-5" data-id="element-17" /> : <ChevronUp className="w-5 h-5" data-id="element-18" />}
              </button>
              <button onClick={() => onPreview(updates)} disabled={!hasChanges} className={`flex items-center px-3 py-1.5 rounded text-sm font-medium transition-colors ${hasChanges ? 'bg-white text-[#1C1C3A] hover:bg-gray-100' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`} data-id="element-19">
                <Eye className="w-4 h-4 mr-1.5" data-id="element-20" />
                Preview
              </button>
              <button onClick={() => onApply(updates)} disabled={!hasChanges} className={`flex items-center px-4 py-1.5 rounded text-sm font-medium transition-colors ${hasChanges ? 'bg-[#D44444] text-white hover:bg-[#B83333]' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`} data-id="element-21">
                <Save className="w-4 h-4 mr-1.5" data-id="element-22" />
                Apply Updates
              </button>
            </div>
          </div>

          {/* Expandable Edit Panel */}
          <AnimatePresence data-id="element-23">
            {isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden bg-gray-50" data-id="element-24">
                <div className="p-6 max-h-[60vh] overflow-y-auto" data-id="element-25">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-id="element-26">
                    {/* Status & Categorization */}
                    <div className="space-y-4" data-id="element-27">
                      <h4 className="text-sm font-semibold text-gray-900 border-b pb-2" data-id="element-28">
                        Status & Categorization
                      </h4>
                      {renderSelect('Condition Status', 'status', [{
                  label: 'Need',
                  value: 'Need'
                }, {
                  label: 'Requested',
                  value: 'Requested'
                }, {
                  label: 'Received',
                  value: 'Received'
                }, {
                  label: 'Review',
                  value: 'Review'
                }, {
                  label: 'Cleared',
                  value: 'Cleared'
                }, {
                  label: 'Waived',
                  value: 'Waived'
                }, {
                  label: 'N/A',
                  value: 'N/A'
                }])}
                      {renderSelect('Category', 'category', [{
                  label: 'Prior to Docs',
                  value: 'Prior to Docs'
                }, {
                  label: 'Prior to Funding',
                  value: 'Prior to Funding'
                }, {
                  label: 'Prior to Closing',
                  value: 'Prior to Closing'
                }, {
                  label: 'Approval',
                  value: 'Approval'
                }, {
                  label: 'Compliance',
                  value: 'Compliance'
                }])}
                      {renderSelect('Priority', 'priority', [{
                  label: 'High',
                  value: 'High'
                }, {
                  label: 'Medium',
                  value: 'Medium'
                }, {
                  label: 'Low',
                  value: 'Low'
                }])}
                    </div>

                    {/* Assignment & Dates */}
                    <div className="space-y-4" data-id="element-29">
                      <h4 className="text-sm font-semibold text-gray-900 border-b pb-2" data-id="element-30">
                        Assignment & Dates
                      </h4>
                      {renderSelect('Assigned To', 'assignedTo', uniqueAssignees.map(a => ({
                  label: a,
                  value: a
                })))}

                      <div className="flex flex-col space-y-1" data-id="element-31">
                        <label className="text-xs font-medium text-gray-500" data-id="element-32">
                          Due Date
                        </label>
                        <input type="date" value={updates.dueDate ? String(updates.dueDate) : ''} onChange={e => handleUpdateChange('dueDate', e.target.value)} className={`block w-full py-1.5 px-3 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444] ${updates.dueDate !== undefined ? 'border-[#D44444] bg-red-50 text-[#D44444] font-medium' : 'border-gray-300 bg-white text-gray-900'}`} data-id="element-33" />
                      </div>

                      <div className="flex flex-col space-y-1" data-id="element-34">
                        <label className="text-xs font-medium text-gray-500" data-id="element-35">
                          Requested Date
                        </label>
                        <input type="date" value={updates.requestedDate ? String(updates.requestedDate) : ''} onChange={e => handleUpdateChange('requestedDate', e.target.value)} className={`block w-full py-1.5 px-3 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444] ${updates.requestedDate !== undefined ? 'border-[#D44444] bg-red-50 text-[#D44444] font-medium' : 'border-gray-300 bg-white text-gray-900'}`} data-id="element-36" />
                      </div>

                      {renderSelect('Source', 'source', [{
                  label: 'Underwriter',
                  value: 'Underwriter'
                }, {
                  label: 'Processor',
                  value: 'Processor'
                }, {
                  label: 'Loan Officer',
                  value: 'Loan Officer'
                }, {
                  label: 'Compliance',
                  value: 'Compliance'
                }])}
                    </div>

                    {/* Visibility & Communication */}
                    <div className="space-y-4" data-id="element-37">
                      <h4 className="text-sm font-semibold text-gray-900 border-b pb-2" data-id="element-38">
                        Visibility & Communication
                      </h4>
                      {renderSelect('Include In Next Email?', 'includeInNextEmail', booleanOptions)}
                      {renderSelect('Borrower Can See In Portal?', 'borrowerCanSeeInPortal', booleanOptions)}
                      {renderSelect('Sent to Underwriter?', 'sentToUnderwriter', booleanOptions)}
                    </div>

                    {/* Notes */}
                    <div className="space-y-4" data-id="element-39">
                      <h4 className="text-sm font-semibold text-gray-900 border-b pb-2" data-id="element-40">
                        Notes
                      </h4>
                      <div className="flex flex-col space-y-1" data-id="element-41">
                        <label className="text-xs font-medium text-gray-500" data-id="element-42">
                          Append to Notes
                        </label>
                        <textarea rows={4} value={updates.notes ? String(updates.notes) : ''} onChange={e => handleUpdateChange('notes', e.target.value)} placeholder="Text entered here will replace existing notes..." className={`block w-full py-2 px-3 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#D44444] focus:border-[#D44444] resize-none ${updates.notes !== undefined ? 'border-[#D44444] bg-red-50 text-[#D44444]' : 'border-gray-300 bg-white text-gray-900'}`} data-id="element-43" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>}
    </AnimatePresence>;
}